import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import { AccountTypes } from '../../components/changeUser/accountTypes.enum';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { RootState, useAppSelector } from '../../state/hooks';
import {
  ConneqtRepository,
  PlayerRepository,
  TeamRepository,
} from '../../state/reduxRepositories';
import { store } from '../../state/store';
import {
  Player,
  PlayerCreatePayload,
  PlayerDataTableList,
} from '../../state/reduxRepositories/players/player.interfaces';
import { maxNumberOfPlayers } from '../../config';
import { DownloadPlayerTemplateService } from '../../state/reduxRepositories/downloadPlayerTemplate/downloadPlayerTemplate.services';
import {
  Conneqt,
  ConneqtStatus,
} from '../../state/reduxRepositories/conneqt/conneqt.interfaces';
import { Team } from '../../state/reduxRepositories/teams/team.interfaces';

export interface NewPlayerInputs {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  guardianEmail?: string;
}
@injectable()
export class AddPlayerDomain {
  constructor(
    private authService: AuthService,
    private playerRepository: PlayerRepository,
    private conneqtRepository: ConneqtRepository,
    private teamRepository: TeamRepository,
    private downloadPlayerTemplateService: DownloadPlayerTemplateService,
  ) {}

  useAccountTypeSelector = (): AccountTypes | undefined => {
    return useAppSelector(this.authService.loggedInUserSelector)?.accountType;
  };

  usePlayersSelector = () => {
    return useAppSelector(this.playerRepository.selectors.resourcesSelector);
  };

  dispatchDownloadPlayerTemplate = (): void => {
    return this.downloadPlayerTemplateService.dispatchDownloadPlayerTemplate();
  };

  useDownloadPlayerTemplateSelector = (): string => {
    return useAppSelector(
      this.downloadPlayerTemplateService.usePlayerTemplateUrlSelector,
    );
  };

  useIsSyncingSelector = (): boolean => {
    return useAppSelector(this.authService.isSyncingSelector);
  };

  useMaxNumberOfPlayersReachedSelector = (): boolean => {
    return useAppSelector((state: RootState) => {
      // TODO: check if this setup work in all cases i.e. if user or players change and nothing execute the functional component will this return a new value? Probably not.
      const loggedInUser = this.authService.loggedInUserSelector(state);
      if (!loggedInUser) {
        return true;
      }

      const players = this.playerRepository.selectors.resourcesSelector(state);
      return (
        loggedInUser.accountType === AccountTypes.Guardian &&
        players.length >= maxNumberOfPlayers
      );
    });
  };

  isUnique = (newPlayer: PlayerCreatePayload) => {
    const players = this.playerRepository.selectors.resourcesSelector(
      store.getState(),
    );
    // Ensure we don't create a player with exactly the same name and dob
    return !players.some((existingPlayer) =>
      this.findDuplicatePlayer(newPlayer, existingPlayer),
    );
  };

  isValidDOB = (birthday: string) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(birthday)) return false;

    const [year, month, day] = birthday.split('-').map(Number);
    const currentYear = new Date().getFullYear();

    if (
      year >= currentYear ||
      currentYear - year < 6 ||
      currentYear - year > 80 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return false;
    }

    return true;
  };

  private findDuplicatePlayer = (
    player: PlayerCreatePayload,
    existingPlayer: Player,
  ): boolean => {
    return (
      this.compareStringsCaseInsensitive(
        player.firstName,
        existingPlayer.firstName,
      ) &&
      this.compareStringsCaseInsensitive(
        player.lastName,
        existingPlayer.lastName,
      ) &&
      player.dateOfBirth === existingPlayer.dateOfBirth
    );
  };

  private compareStringsCaseInsensitive = (
    str1: string | undefined,
    str2: string | undefined,
  ): boolean => {
    return (
      (str1?.trim()?.toLowerCase() ?? '') ===
      (str2?.trim()?.toLowerCase() ?? '')
    );
  };

  usePlayerListDataSelector = (): PlayerDataTableList[] => {
    return useAppSelector(this.createPlayerListDataSelector());
  };

  createPlayerListDataSelector = () => {
    return createSelector(
      this.conneqtRepository.selectors.resourcesSelector,
      this.playerRepository.selectors.resourcesSelector,
      this.teamRepository.selectors.resourcesSelector,
      (
        conneqt: Conneqt[],
        players: Player[],
        teams: Team[],
      ): PlayerDataTableList[] => {
        return players.map((player) => {
          return {
            playerName: `${player.firstName} ${player.lastName}`,
            playerId: player.id,
            dob: player.dateOfBirth,
            clearStatus: player.clearedToPlay,
            guardianEmail: conneqt
              ?.filter((con) => con.requestPlayerId === player.id)
              .map((guardian) => guardian.responseEmail)
              .join(','),
            team: teams?.find((team) => team.playerIds.includes(player.id))
              ?.name,
            conneqtedStatus:
              conneqt.filter(
                (con) =>
                  con.status === ConneqtStatus.Conneqted &&
                  (con.requestPlayerId === player.id ||
                    con.responsePlayerId === player.id),
              ).length > 0,
          };
        });
      },
    );
  };
}
